import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  FileText,
  UserCheck,
  Play,
  CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChangeRequestWorkflowProps {
  status: string;
  requestedAt?: string | null;
  reviewedAt?: string | null;
  approvedAt?: string | null;
  executedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
}

const workflowSteps = [
  {
    id: 'pending',
    label: 'Request Submitted',
    icon: FileText,
    description: 'Request has been created and is awaiting review',
  },
  {
    id: 'under_review',
    label: 'Under Review',
    icon: UserCheck,
    description: 'Request is being reviewed by administrators',
  },
  {
    id: 'approved',
    label: 'Approved',
    icon: CheckCircle,
    description: 'Request has been approved and is ready for execution',
  },
  {
    id: 'executed',
    label: 'Executed',
    icon: Play,
    description: 'Request has been successfully executed',
  },
];

const rejectedStep = {
  id: 'rejected',
  label: 'Rejected',
  icon: XCircle,
  description: 'Request has been rejected',
};

const failedStep = {
  id: 'failed',
  label: 'Failed',
  icon: AlertTriangle,
  description: 'Execution failed',
};

const cancelledStep = {
  id: 'cancelled',
  label: 'Cancelled',
  icon: XCircle,
  description: 'Request was cancelled',
};

export function ChangeRequestWorkflow({
  status,
  requestedAt,
  reviewedAt,
  approvedAt,
  executedAt,
  rejectedAt,
  rejectionReason,
}: ChangeRequestWorkflowProps) {
  const getCurrentStepIndex = () => {
    switch (status) {
      case 'pending':
        return 0;
      case 'under_review':
        return 1;
      case 'approved':
        return 2;
      case 'executed':
        return 3;
      case 'rejected':
        return -1; // Special case
      case 'failed':
        return -2; // Special case
      case 'cancelled':
        return -3; // Special case
      default:
        return 0;
    }
  };

  const currentStepIndex = getCurrentStepIndex();
  const isRejected = status === 'rejected';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';
  const isErrorState = isRejected || isFailed || isCancelled;

  const getStepStatus = (stepIndex: number) => {
    if (isErrorState) {
      if (isRejected && stepIndex < 2) return 'completed';
      if (isFailed && stepIndex < 3) return 'completed';
      if (isCancelled) return 'completed';
      return 'error';
    }

    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'pending';
  };

  const getStepDate = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return requestedAt;
      case 1:
        return reviewedAt;
      case 2:
        return approvedAt;
      case 3:
        return executedAt;
      default:
        return null;
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProgressPercentage = () => {
    if (isErrorState) {
      if (isRejected) return 50; // Stopped at review
      if (isFailed) return 75; // Stopped at execution
      if (isCancelled) return 25; // Cancelled early
      return 0;
    }
    return ((currentStepIndex + 1) / workflowSteps.length) * 100;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Request Workflow</CardTitle>
        <CardDescription>
          Track the progress of this change management request through the approval and execution process
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{Math.round(getProgressPercentage())}%</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Workflow Steps */}
        <div className="space-y-4">
          {workflowSteps.map((step, index) => {
            const stepStatus = getStepStatus(index);
            const stepDate = getStepDate(index);
            const Icon = step.icon;
            const isCompleted = stepStatus === 'completed';
            const isCurrent = stepStatus === 'current';
            const isPending = stepStatus === 'pending';

            return (
              <div key={step.id} className="relative">
                {/* Connector Line */}
                {index > 0 && (
                  <div
                    className={cn(
                      'absolute left-5 top-0 w-0.5 h-6 -translate-y-full',
                      isCompleted ? 'bg-green-500' : 'bg-muted'
                    )}
                  />
                )}

                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                      isCompleted && 'bg-green-500 border-green-500 text-white',
                      isCurrent && 'bg-blue-500 border-blue-500 text-white',
                      isPending && 'bg-muted border-muted-foreground/20 text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'font-medium',
                          isCompleted && 'text-green-600',
                          isCurrent && 'text-blue-600',
                          isPending && 'text-muted-foreground'
                        )}
                      >
                        {step.label}
                      </span>
                      {isCurrent && (
                        <Badge variant="default" className="text-xs">
                          Current
                        </Badge>
                      )}
                      {isCompleted && (
                        <Badge variant="outline" className="text-xs border-green-500 text-green-600">
                          Completed
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                    {stepDate && (
                      <p className="text-xs text-muted-foreground">
                        {formatDate(stepDate)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Error States */}
          {isErrorState && (
            <div className="relative pt-4">
              <div className="absolute left-5 top-0 w-0.5 h-4 bg-muted" />
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2',
                    isRejected && 'bg-red-500 border-red-500 text-white',
                    isFailed && 'bg-orange-500 border-orange-500 text-white',
                    isCancelled && 'bg-gray-500 border-gray-500 text-white'
                  )}
                >
                  {(isRejected || isCancelled) && rejectedStep.icon && (
                    <rejectedStep.icon className="h-5 w-5" />
                  )}
                  {isFailed && failedStep.icon && (
                    <failedStep.icon className="h-5 w-5" />
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'font-medium',
                        isRejected && 'text-red-600',
                        isFailed && 'text-orange-600',
                        isCancelled && 'text-gray-600'
                      )}
                    >
                      {isRejected && rejectedStep.label}
                      {isFailed && failedStep.label}
                      {isCancelled && cancelledStep.label}
                    </span>
                    <Badge
                      variant="destructive"
                      className="text-xs"
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isRejected && rejectedStep.description}
                    {isFailed && failedStep.description}
                    {isCancelled && cancelledStep.description}
                  </p>
                  {rejectionReason && (
                    <p className="text-sm text-red-600 mt-2">
                      <strong>Reason:</strong> {rejectionReason}
                    </p>
                  )}
                  {(rejectedAt || executedAt) && (
                    <p className="text-xs text-muted-foreground">
                      {formatDate(rejectedAt || executedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

